import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography } from '@mui/material';

const DIVIDER_STYLES = [
  { id: 'solid', label: 'Solid', borderStyle: 'solid' },
  { id: 'dashed', label: 'Dashed', borderStyle: 'dashed' },
  { id: 'dotted', label: 'Dotted', borderStyle: 'dotted' },
  { id: 'double', label: 'Double', borderStyle: 'double' },
];

const DIVIDER_THICKNESS = [
  { id: 'thin', label: 'Thin', px: 1 },
  { id: 'medium', label: 'Medium', px: 2 },
  { id: 'thick', label: 'Thick', px: 4 },
];

const DIVIDER_COLORS = [
  { id: 'black', label: 'Black', color: '#000000' },
  { id: 'gray', label: 'Gray', color: '#808080' },
  { id: 'light-gray', label: 'Light Gray', color: '#CCCCCC' },
  { id: 'dark-gray', label: 'Dark Gray', color: '#333333' },
  { id: 'gold', label: 'Gold', color: '#D4AF37' },
  { id: 'navy', label: 'Navy', color: '#001F3F' },
  { id: 'brown', label: 'Brown', color: '#8B4513' },
  { id: 'red', label: 'Red', color: '#DC143C' },
  { id: 'blue', label: 'Blue', color: '#0066CC' },
];

export default function DividerPicker({ open, onClose, onInsert }) {
  const [selectedStyle, setSelectedStyle] = useState('solid');
  const [selectedThickness, setSelectedThickness] = useState('medium');
  const [selectedColor, setSelectedColor] = useState('black');

  const handleInsert = () => {
    const style = DIVIDER_STYLES.find(s => s.id === selectedStyle);
    const thickness = DIVIDER_THICKNESS.find(t => t.id === selectedThickness);
    const color = DIVIDER_COLORS.find(c => c.id === selectedColor);

    onInsert({
      style: style.borderStyle,
      thickness: thickness.px,
      color: color.color,
    });

    onClose();
  };

  const currentStyle = DIVIDER_STYLES.find(s => s.id === selectedStyle);
  const currentThickness = DIVIDER_THICKNESS.find(t => t.id === selectedThickness);
  const currentColor = DIVIDER_COLORS.find(c => c.id === selectedColor);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Insert Divider</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 3 }}>
        
        {/* Preview */}
        <Box sx={{ border: '1px solid #eee', p: 3, borderRadius: 1, bgcolor: '#fafafa' }}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
            Preview:
          </Typography>
          <hr
            style={{
              borderTop: `${currentThickness.px}px ${currentStyle.borderStyle} ${currentColor.color}`,
              borderBottom: 'none',
              borderLeft: 'none',
              borderRight: 'none',
            }}
          />
        </Box>

        {/* Style Selection */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Line Style
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
            {DIVIDER_STYLES.map(style => (
              <Button
                key={style.id}
                variant={selectedStyle === style.id ? 'contained' : 'outlined'}
                onClick={() => setSelectedStyle(style.id)}
                sx={{
                  background: selectedStyle === style.id ? '#D4AF37' : 'transparent',
                  color: selectedStyle === style.id ? '#1A1A1A' : '#333',
                  fontWeight: selectedStyle === style.id ? 600 : 400,
                  borderColor: '#ddd',
                  '&:hover': {
                    background: selectedStyle === style.id ? '#F5D76E' : '#f5f5f5',
                  }
                }}
              >
                {style.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Thickness Selection */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Thickness
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
            {DIVIDER_THICKNESS.map(thickness => (
              <Button
                key={thickness.id}
                variant={selectedThickness === thickness.id ? 'contained' : 'outlined'}
                onClick={() => setSelectedThickness(thickness.id)}
                sx={{
                  background: selectedThickness === thickness.id ? '#D4AF37' : 'transparent',
                  color: selectedThickness === thickness.id ? '#1A1A1A' : '#333',
                  fontWeight: selectedThickness === thickness.id ? 600 : 400,
                  borderColor: '#ddd',
                  '&:hover': {
                    background: selectedThickness === thickness.id ? '#F5D76E' : '#f5f5f5',
                  }
                }}
              >
                {thickness.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Color Selection */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Color
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1.5 }}>
            {DIVIDER_COLORS.map(color => (
              <Box
                key={color.id}
                onClick={() => setSelectedColor(color.id)}
                sx={{
                  width: '100%',
                  aspectRatio: '1',
                  backgroundColor: color.color,
                  border: selectedColor === color.id ? '3px solid #D4AF37' : '2px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: selectedColor === color.id ? '0 4px 8px rgba(212, 175, 55, 0.3)' : 'none',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }
                }}
                title={color.label}
              />
            ))}
          </Box>
        </Box>

      </DialogContent>
      <DialogActions sx={{ gap: 1, p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleInsert} variant="contained" sx={{ background: '#D4AF37', color: '#1A1A1A' }}>
          Insert Divider
        </Button>
      </DialogActions>
    </Dialog>
  );
}
